const SIDEBAR_MENU_MOBILE_MQ = window.matchMedia("(max-width: 949px)");
const HOVER_NONE_MQ = window.matchMedia("(hover: none)");

class SidebarCollectionMenu extends HTMLElement {
	static #instances = new Set();
	static #documentListenersBound = false;
	static #submenuResizeObservers = new WeakMap();

	static #usesClickSubmenus() {
		return SIDEBAR_MENU_MOBILE_MQ.matches || HOVER_NONE_MQ.matches;
	}

	static #usesStackedDropdownLayout() {
		return SIDEBAR_MENU_MOBILE_MQ.matches;
	}

	static #applySubmenuMeasuredMaxHeight(submenu) {
		if (!SidebarCollectionMenu.#usesStackedDropdownLayout()) return;
		const h = submenu.scrollHeight;
		submenu.style.setProperty("--sidebar-submenu-max", `${Math.max(h, 0)}px`);
	}

	static #attachSubmenuResizeObserver(submenu) {
		if (!window.ResizeObserver || SidebarCollectionMenu.#submenuResizeObservers.has(submenu)) {
			return;
		}
		const ro = new ResizeObserver(() => {
			if (submenu.getAttribute("aria-hidden") !== "false") return;
			if (!SidebarCollectionMenu.#usesStackedDropdownLayout()) return;
			SidebarCollectionMenu.#applySubmenuMeasuredMaxHeight(submenu);
		});
		ro.observe(submenu);
		SidebarCollectionMenu.#submenuResizeObservers.set(submenu, ro);
	}

	static #queueMobileSubmenuMeasure(submenu) {
		if (!SidebarCollectionMenu.#usesStackedDropdownLayout()) return;
		requestAnimationFrame(() => {
			requestAnimationFrame(() => {
				SidebarCollectionMenu.#applySubmenuMeasuredMaxHeight(submenu);
			});
		});
	}

	static #bindDocumentListeners() {
		if (SidebarCollectionMenu.#documentListenersBound) return;
		SidebarCollectionMenu.#documentListenersBound = true;

		document.addEventListener(
			"pointerdown",
			(e) => {
				if (!SidebarCollectionMenu.#usesClickSubmenus()) return;
				if (e.target.closest("sidebar-collection-menu")) return;
				SidebarCollectionMenu.#instances.forEach((host) => host.#clearSubmenuOpenState());
			},
			true
		);

		const onLayoutMediaChange = () => {
			SidebarCollectionMenu.#instances.forEach((host) => {
				host.#clearSubmenuOpenState();
				host.#syncSidebarListToViewport();
				setTimeout(() => host.#setupSubmenusPos(), 300);
			});
		};
		SIDEBAR_MENU_MOBILE_MQ.addEventListener("change", onLayoutMediaChange);
		HOVER_NONE_MQ.addEventListener("change", onLayoutMediaChange);

		window.addEventListener("resize", () => {
			setTimeout(() => {
				SidebarCollectionMenu.#instances.forEach((host) => host.#setupSubmenusPos());
			}, 300);
		});
	}

	#abort = null;

	#clearSubmenuOpenState() {
		this.querySelectorAll(".sidebar-menu .sidebar-menu__item__submenu").forEach((sub) => {
			sub.setAttribute("aria-hidden", "true");
			sub.style.removeProperty("--sidebar-submenu-max");
		});
	}

	#setupSubmenusPos() {
		if (!window.matchMedia("(min-width: 750px)").matches) return;
		this.querySelectorAll(".sidebar-menu__item__submenu").forEach((submenu) => {
			submenu.classList.remove("right-align");
			const rect = submenu.getBoundingClientRect();
			const offsetLeftFromPage = rect.left + window.pageXOffset;
			const spaceRight = window.innerWidth - offsetLeftFromPage - submenu.offsetWidth;
			submenu.classList.toggle("right-align", spaceRight < 5);
		});
	}

	#syncSidebarListToViewport() {
		const mobile = SIDEBAR_MENU_MOBILE_MQ.matches;
		this.querySelectorAll(".sidebar-menu__header--toggle").forEach((btn) => {
			const id = btn.getAttribute("aria-controls");
			const list = id ? document.getElementById(id) : null;
			const sidebar = btn.closest(".sidebar-menu");
			if (!list || !sidebar) return;
			if (mobile) {
				btn.disabled = false;
				btn.setAttribute("aria-expanded", "false");
				list.removeAttribute("hidden");
				list.setAttribute("aria-hidden", "true");
				sidebar.classList.remove("sidebar-menu--list-open");
			} else {
				btn.disabled = true;
				btn.setAttribute("aria-expanded", "true");
				list.removeAttribute("hidden");
				list.setAttribute("aria-hidden", "false");
				sidebar.classList.add("sidebar-menu--list-open");
			}
		});
	}

	#bindMenuListToggle(signal) {
		this.querySelectorAll(".sidebar-menu__header--toggle").forEach((btn) => {
			btn.addEventListener(
				"click",
				() => {
					if (!SIDEBAR_MENU_MOBILE_MQ.matches) return;

					const expanded = btn.getAttribute("aria-expanded") === "true";
					const nextOpen = !expanded;
					const id = btn.getAttribute("aria-controls");
					const list = id ? document.getElementById(id) : null;
					const sidebar = btn.closest(".sidebar-menu");
					if (!list || !sidebar) return;

					if (!nextOpen) {
						this.#clearSubmenuOpenState();
					}

					btn.setAttribute("aria-expanded", String(nextOpen));
					list.removeAttribute("hidden");
					sidebar.classList.toggle("sidebar-menu--list-open", nextOpen);
					list.setAttribute("aria-hidden", String(!nextOpen));
				},
				{ signal }
			);
		});
	}

	#bindSubmenuLinks(signal) {
		this.querySelectorAll(".sidebar-menu a.sidebar-menu__link").forEach((link) => {
			const submenu = link.nextElementSibling;
			if (!submenu || !submenu.classList.contains("sidebar-menu__item__submenu")) {
				return;
			}
			link.addEventListener(
				"click",
				(e) => {
					if (!SidebarCollectionMenu.#usesClickSubmenus()) return;
					e.preventDefault();
					e.stopPropagation();
					const currentlyShown = submenu.getAttribute("aria-hidden") === "false";
					if (currentlyShown) {
						submenu.setAttribute("aria-hidden", "true");
						submenu.style.removeProperty("--sidebar-submenu-max");
					} else {
						submenu.setAttribute("aria-hidden", "false");
						SidebarCollectionMenu.#queueMobileSubmenuMeasure(submenu);
						SidebarCollectionMenu.#attachSubmenuResizeObserver(submenu);
					}
					if (!SidebarCollectionMenu.#usesStackedDropdownLayout()) {
						setTimeout(() => this.#setupSubmenusPos(), 0);
					}
				},
				{ signal }
			);
		});
	}

	#init(signal) {
		if (!this.querySelector(".sidebar-menu")) return;
		this.#bindMenuListToggle(signal);
		this.#syncSidebarListToViewport();
		this.#bindSubmenuLinks(signal);
		if (this.querySelector(".sidebar-menu__item__submenu")) {
			this.#setupSubmenusPos();
			this.querySelectorAll(".sidebar-menu .sidebar-menu__item__submenu").forEach((sub) => {
				SidebarCollectionMenu.#attachSubmenuResizeObserver(sub);
				if (sub.getAttribute("aria-hidden") === "false") {
					SidebarCollectionMenu.#queueMobileSubmenuMeasure(sub);
				}
			});
		}
	}

	connectedCallback() {
		this.#abort?.abort();
		this.#abort = new AbortController();
		const { signal } = this.#abort;

		SidebarCollectionMenu.#instances.add(this);
		SidebarCollectionMenu.#bindDocumentListeners();
		this.#init(signal);
	}

	disconnectedCallback() {
		SidebarCollectionMenu.#instances.delete(this);
		this.#abort?.abort();
		this.#abort = null;
	}
}

if (!customElements.get("sidebar-collection-menu")) {
	customElements.define("sidebar-collection-menu", SidebarCollectionMenu);
}
